import { serverSideTranslations } from "@/libs/i18n/serverSideTranslations";
import { GetServerSideProps } from "next";
import { BhapticsContent } from "@/store/reducers/contents";
import { generateGameSeo } from "@/config/seo";
import GameMetadataSection from "@/v2-components/pages/games/detail/GameMetadataSection";
import { cn } from "@/libs/utils";
import GameInfo from "@/v2-components/pages/games/detail/GameInfo";
import GameGuideButtons from "@/v2-components/pages/games/detail/GameGuideButtons";
import HapticFeedbackReview from "@/v2-components/pages/games/detail/HapticFeedbackReview";
import HapticEffects from "@/v2-components/pages/games/detail/HapticEffects";
import HapticSummaryCard from "@/v2-components/pages/games/detail/HapticSummaryCard";
import { SupportTypeUtil } from "@/utils/SupportTypeUtil";
import {
  getHapticSummary,
  isHapticCardEnabled,
  type HapticSummary,
} from "@/libs/games/haptic-summary.server";
import { isProd } from "@/Env";
import GameUpdates from "@/v2-components/pages/games/detail/GameUpdates";
import ReviewVideos from "@/v2-components/pages/games/detail/ReviewVideos";
import RecommendedGames from "@/v2-components/pages/games/detail/RecommendedGames";
import Inquiry from "@/v2-components/pages/games/detail/Inquiry";
import SupportedDevices from "@/v2-components/pages/games/detail/SupportedDevices";
import LoLChampions from "@/v2-components/pages/games/detail/LoLChampions";
import { dehydrate } from "@tanstack/react-query";
import NextError from "next/error";
import {
  getDehydratedState,
  getQueryClient,
  mergeDehydratedStates,
} from "@/utils/ReactQuery";
import {
  GameApiServer,
  GameQueryOptionsServer,
  isUpstreamUnavailable,
} from "@/api/game-api.server";
import { ProductQuery } from "@/services/product/ProductQuery";
import React, { useState } from "react";
import { useGame } from "@/api/game-api";
import BhapticsFaqSection, {
  FaqHelp,
  FaqNotionTags,
} from "@/components/faq/BhapticsFaqSection";
import { useMount } from "react-use";
import { analyticsInstance } from "@/libs/analytics";
import FbPixel from "@/utils/FbPixel";
import { useCurrency } from "@/hooks/useCurrency";
import { useFunnelValueEffect } from "@/libs/analytics/use-funnel-value-effect";
import GameDetailPageTemplate from "@/components/templates/GameDetailPageTemplate";
import { getShippableGeo } from "@/libs/getGeo";
import { getServerSideCountryCode } from "@/utils/LocaleUtil";
import { MENU_NAME } from "@/v2-components/global-navigation-bar/_const/navigation-menus";
import GamesSubNav, {
  type Section,
} from "@/v2-components/pages/games/GamesSubNav";
import { VR_PLATFORM_VALUES } from "@/constants/game-filter-options";

type GameDetailPageProps = {
  slug: string;
  countryCode: string;
  /** AI 햅틱 요약(베타) — 그 게임 것만. 없거나 미승인이면 null(`getHapticSummary`) */
  hapticSummary?: HapticSummary | null;
  /** 햅틱 카드(요약 · 지원 타입 안내)를 그리나 — 실서비스 공개 전 게이트(`isHapticCardEnabled`) */
  hapticCardEnabled?: boolean;
  /**
   * 업스트림 일시 장애(#5318). 설정되면 상세 대신 상태 페이지를 렌더한다 —
   * `notFound: true`(404)로 접으면 실존 페이지가 색인 해제될 수 있어 5xx 로 응답한다.
   */
  errorStatus?: number;
};

/**
 * ⚠️ 에러 분기를 **래퍼로 분리**한다. 아래 `GameDetailPage` 안에서 early return 하면
 * `useGame` 등 훅보다 앞선 조건부 반환이 되어 `react-hooks/rules-of-hooks` 위반이다.
 * 업스트림 실패 시에는 `game` 이 없어 `game.hapticEffects` 접근이 터지므로(그 예외를 막으려
 * 404 로 접은 것이 #4915) 훅을 아예 실행하지 않아야 한다.
 */
export default function GameDetailRoute(props: GameDetailPageProps) {
  if (props.errorStatus) return <NextError statusCode={props.errorStatus} />;
  return <GameDetailPage {...props} />;
}

function GameDetailPage({
  slug,
  countryCode,
  hapticSummary = null,
  hapticCardEnabled = false,
}: Omit<GameDetailPageProps, "errorStatus">) {
  const { data } = useGame(slug);
  const game = data as BhapticsContent;
  const currency = useCurrency();
  const hasEffects = (game.hapticEffects?.length ?? 0) > 0;
  // 이펙트 「전체 보기」 시트 — 햅틱 요약 카드의 줄이 그 근거 카테고리를 강조해 연다
  const [effectSheet, setEffectSheet] = useState<{
    open: boolean;
    highlight: string[];
  }>({ open: false, highlight: [] });
  const [top, setTop] = useState(0);

  const isLoL = slug === "league-of-legends";

  /**
   * 이 게임이 VR 섹션인가 PC 섹션인가 — 서브내비 탭 세트·활성 탭을 정한다.
   *
   * ## ★「VR 우선」은 임의 선택이다 — 데이터로는 못 가른다
   *
   * 두 목록의 facet 이 **배타적이지 않다**. `/games/vr/games` 는
   * `VR_PLATFORM_FILTERS`(= `VR_PLATFORM_VALUES`, `game-api.ts:22`), `/games/pc/games` 는
   * `["pc"]` 로 거르는데(`useBhapticsContents.ts` `usePathFacets`), **둘 다 가진 게임이 있다.**
   * 그런 게임은 양쪽 목록에 뜨지만 상세 URL 은 하나(`/games/experiences/{slug}`)라
   * 여기서 한쪽을 골라야 하고, **게임 데이터는 사용자가 어느 목록에서 왔는지 모른다.**
   * 그래서 PC 목록에서 눌러도 VR 탭이 켜진다 — 규칙의 한계지 버그가 아니다.
   *
   * prod 실측(2026-09-01, `by-filters` 를 각 목록의 facet 그대로 호출):
   *
   * ```
   * /games/pc/games   43     /games/vr/games  319     교집합 3
   *   minecraft              pc → minecraft.net  ·  steam → vivecraft.org   ← 서로 다른 링크
   *   no-mans-sky            둘 다 app/275850 (네이티브 VR 지원 타이틀)
   *   resident-evil-requiem  둘 다 app/3764200 — ★데이터 오등록(PC 인데 SteamVR 이 붙음)
   * ```
   *
   * 앞의 둘은 **정당한 이중 등록**이라 데이터를 고쳐도 남는다. 특히 `minecraft` 는 PC 판과
   * VR 모드(Vivecraft)를 다른 링크로 등록한 것이라 「하나로 합치기」가 답이 아니다.
   *
   * ## 겹침이 늘면 어떻게 하나
   *
   * 지금은 2~3건이라 수용한다. 늘어나면 **링크에 출처를 싣는다**(PC 표면의 카드에
   * `?from=pc`, 여기서 쿼리 우선·없으면 아래 판정으로 폴백). URL 을 `/games/{vr,pc}/games/{slug}`
   * 로 쪼개는 안은 prod sitemap **377개** 이동 + canonical + locale prefix
   * (`docs/pitfalls.md` — Pages Router 리다이렉트에 `getLocalePrefix` 누락)까지 딸려서
   * 이 문제 하나로는 수지가 안 맞는다.
   */
  /**
   * ★**`p.enabled` 를 보지 않는다 — 일부러다** (R1 N2).
   *
   * 형제 판정은 본다(`api/game-api.ts` — `p.platform === platform && p.enabled`).
   * 여기서 안 보는 이유는 **지금 검증할 수 없기 때문**이다. prod 실측(2026-09-02):
   *
   * ```
   *   supportedPlatforms 항목 559개 중 enabled 값 분포   { true: 559 }
   *   `enabled` 키가 없는 항목                            0
   *   `enabled` 를 봤을 때 판정이 갈리는 게임              0 / 379
   * ```
   *
   * 즉 넣어도 오늘은 **아무 게임도 안 바뀌고**, 뮤턴트를 심어도 관측되지 않는다.
   * 이 리포 규칙이 그 자리를 짚는다 — 검증되지 않는 방어는 「이게 없으면 샌다」는 잘못된
   * 신호를 주고 서로의 뮤턴트를 가린다.
   *
   * ⚠️ **전제가 바뀌면 넣는다.** VR 플랫폼이 `enabled: false` 로 등록되기 시작하면
   * 여기는 「VR」이라 하는데 **VR 목록엔 그 게임이 없는** 상태가 된다(목록 쪽은
   * `enabled` 를 보므로). 그때는 `&& p.enabled` 한 조각이고, 그 시점엔 갈리는 데이터가
   * 있으니 가드도 검증된다.
   *
   * ## ★이 판단은 **이 자리 한정**이다 — 리포 전역 정책이 아니다
   *
   * #5715 가 같은 페이지의 **표시·링크 표면 세 곳**(상세 사이드바 웹·웹뷰, 웹뷰 히어로
   * 아이콘)과 **배지 판정 두 곳**(`SupportTypeUtil`·`ContentsService` 의
   * `getSupportType`)에는 `enabled` 필터를 **넣었다.** prod 조건(0건)이 같은데 결론이
   * 반대인 이유는 **관측 가능성**이다:
   *
   * ```
   *   표시·링크·배지   픽스처만으로 갈린다 → 뮤턴트가 관측된다 → 넣는다
   *   여기(분류)       갈리는 데이터가 없다 → 뮤턴트가 관측 안 된다 → 안 넣는다
   * ```
   *
   * 즉 기준은 「prod 에 데이터가 있나」가 아니라 **「그 방어를 검증할 수 있나」** 다.
   * 위 「전제가 바뀌면」 조건이 참이 되는 순간 이 셋을 **함께** 보면 된다.
   */
  const gameSection: Section = (game.supportedPlatforms ?? []).some((p) =>
    (VR_PLATFORM_VALUES as readonly string[]).includes(p.platform),
  )
    ? "vr"
    : "pc";

  /**
   * 게임키 `view_item` — 표시 통화로, **값 축이 확정된 뒤** (#5190 · R1).
   *
   * 통화가 `priceList` 에 없으면 두 트래커 모두 USD 로 폴백하고 **폴백한 통화를 라벨에
   * 싣는다** — 규칙 정본은 `Analytics.gameResellToViewItems` 주석.
   *
   * ★초판은 `useMount` 였다. `useMount` 는 정의상 재발화가 없어 `currency` 가 첫 값으로
   * 굳는데, 첫 렌더에는 `globalEEnabled` 가 아직 로딩(`null` → `geEnabled=false`)이라
   * **flag ON 시** 유로존/GB 세션의 `view_item` 만 USD 폴백으로 나가고 `add_to_cart` 부터
   * EUR/GBP 가 된다. flag OFF 인 현재 prod 에서는 증상이 없지만 **flag ON 전 게이트**라
   * 지금 닫는다. 기전은 `use-funnel-value-effect.ts` docblock.
   */
  useFunnelValueEffect(game.gameResell ? slug : null, () => {
    analyticsInstance?.viewGameResell(game.gameResell, currency);
    FbPixel.viewGameResell(game.gameResell, currency);
  });

  return (
    <GameDetailPageTemplate
      countryCode={countryCode}
      /**
       * 시안 `29889:101361`(데스크탑) · `30035:18958`(모바일) — 상세 상단은 3탭이다.
       * 넘기지 않으면 템플릿이 **옛 탭바**(검색창 + Home·AI Powered·PC Game·Sim Racing·
       * Meta Quest·Steam VR·Pico·PSVR2·Audio-to-Haptics 9개)를 그린다.
       *
       * ★활성 탭은 **그 게임이 속한 섹션의 목록 탭**이다(시안 `30035:18960` 이 상세에서
       * 「VR Games」를 활성으로 그린다). 상세 경로가 `/games/experiences/{slug}` 라
       * `getSubNavTabs` 의 경로 판정이 안 먹어(기본값 PC) `section` 을 넘긴다.
       */
      tabBar={<GamesSubNav section={gameSection} />}
      /* ★GNB 도 같은 값을 받는다 — 경로(`/games/experiences/*`)로는 못 가른다.
         서브내비만 받고 GNB 가 안 받아, PC 게임 상세에서 VR 이 볼드였다(2026-09-02). */
      activeMenuOverride={
        gameSection === "vr" ? MENU_NAME.VR_GAMES : MENU_NAME.PC_GAMES
      }
      onHeaderChange={({ height }) => {
        if (top !== height) {
          setTop(height);
        }
      }}
    >
      <GameMetadataSection key={game.id} game={game} />

      {/**
       * `gd-body` — 시안 `29889:101424`. 히어로 아래부터 **2열**이다.
       *
       * ```
       *   gd-main   x=240  w=952
       *   gd-aside  x=1216 w=464   → main 끝(1192)에서 갭 24
       *   히어로 끝 640 → gd-body y=730 = 갭 90
       * ```
       *
       * ★**목차 바(TOC)를 걷었다.** 시안 4판 어디에도 없다(`29889:101358`·`102086`·
       * `101719`·`30035:21866` 메타 전수). 같이 죽는 기계를 남기지 않으려고 상태
       * (`currentMenu`)·`menus`·`.observe` 스크롤 핸들러·`MenuID` 앵커를 함께 걷었다 —
       * UI 만 지우면 옵저버가 매 스크롤마다 빈 목록을 훑는다(`docs/pitfalls.md` #27
       * 「UI 표면을 갈아치우면 가드·이벤트가 조용히 사라진다」의 반대 방향).
       * 앵커(`#game-detail-*`)를 참조하는 곳은 리포·테스트에 **0건**이라 딥링크가 죽지 않는다.
       *
       * ⚠️ 시안에 없지만 **남기기로 한 것**(사용자 판단): `SupportedDevices` ·
       * `GamesDetailFaq` · `Inquiry` · `LoLChampions`. 시안은 목업이라 조건부 섹션이
       * 안 뜬 것으로 읽었고, 기능을 조용히 잃지 않는 쪽을 골랐다.
       */}
      {/**
       * ★**섹션 간 간격은 `gap` 으로 준다** — 종전엔 섹션마다 `pb-16 laptop:pb-24`(64/96)
       * 였는데 시안은 **48 / 40** 이다. 값 출처는 좌표가 아니라 CSS 다:
       *
       * ```
       * 데스크탑 `29889:101425` gd-main   flex-col gap-[40px]
       * 모바일   `30035:18962` body       flex-col gap-[48px]
       * 바깥     `29889:101424` gd-body   flex gap-[24px]      ← main ↔ aside (데스크탑)
       * ```
       *
       * `pb` 를 각 섹션에 걸면 **마지막 섹션에도 붙어** 다음 블록과의 간격이 두 번 들어가고,
       * 조건부 섹션이 빠질 때 간격도 같이 사라진다. `gap` 은 둘 다 자동으로 맞는다.
       *
       * ⚠️ 모바일 바깥 간격도 24 → **48** 이다(`gap-12 laptop:gap-6`). 모바일에서는 aside 가
       * 아래로 흐르므로 그 사이도 「섹션 간」이다.
       */}
      <div className="flex w-full justify-center pb-12 pt-12 laptop:pb-[90px] laptop:pt-[90px]">
        <div className="mx-4 flex w-full max-w-screen-max flex-col gap-12 laptop:flex-row laptop:items-start laptop:gap-6">
          {/* gd-main — 시안 952. 좁아지면 남는 폭을 쓴다. 섹션 간 48(모바일) / 40(데스크탑). */}
          <div className="flex min-w-0 flex-1 flex-col gap-12 laptop:max-w-[952px] laptop:gap-10">
            {isLoL && <LoLChampions />}

            {/* AI 햅틱 요약(베타) — 본문 첫 섹션. 숫자는 이펙트 섹션이 맡고 카드는 문장만(HapticSummaryCard).
                요약이 없는 게임은 지원 타입 안내로 그린다 — 원료가 없는 게임도 비워 두지 않는다 */}
            {hapticCardEnabled && (
              <HapticSummaryCard
                slug={game.slug}
                gameName={game.name}
                summary={hapticSummary}
                support={{
                  type: SupportTypeUtil.getSupportType(
                    game.supportedPlatforms ?? [],
                  ),
                }}
                devices={game.compatibleDevices}
                hasEffects={hasEffects}
                effectCategories={(game.hapticEffects ?? []).map(
                  (e) => e.category,
                )}
                onShowEffects={(highlight) =>
                  setEffectSheet({ open: true, highlight })
                }
              />
            )}

            {hasEffects && (
              <HapticEffects
                effects={game.hapticEffects ?? []}
                sheet={{
                  open: effectSheet.open,
                  highlight: effectSheet.highlight,
                  // 「전체 보기」 버튼으로 열면 강조 없이 — 닫을 때 강조도 지운다
                  onOpenChange: (open) =>
                    setEffectSheet({ open, highlight: [] }),
                }}
              />
            )}

            {/**
             * 업데이트 (#5472) — 시안은 `gd-main` **최상단**이다
             * (`29889:101424` 는 이펙트 → 업데이트 순, `30035:21866` 은 업데이트가 첫 섹션).
             * 두 프레임이 갈리는 이유는 후자에 이펙트가 없어서고, **이펙트 다음**이
             * 두 프레임 모두와 모순되지 않는 유일한 자리다.
             *
             * 이력이 없으면 컴포넌트가 `null` 을 반환하므로 컨테이너 `gap` 도 안 먹는다 —
             * prod 379개 중 이력 보유 0건이라 그게 기본 경로다.
             */}
            <GameUpdates slug={game.slug} />

            <HapticFeedbackReview game={game} />

            {game.posts.length > 0 && <ReviewVideos posts={game.posts} />}
          </div>

          {/**
           * `gd-aside` — 시안 `29889:101563`. 폭 464 에 `pl-40` 이라 카드는 424 다.
           * 데스크탑에서만 옆으로 간다 — 모바일은 위 컬럼 아래로 흐른다.
           */}
          {/* 시안 `30035:22039` — aside 는 세로 gap **32**(버튼 묶음 → 카드). */}
          <aside className="flex w-full shrink-0 flex-col gap-8 laptop:w-[464px] laptop:pl-10">
            {/* ★히어로에서 옮겨온 가이드 버튼(#5474, `30035:23140`).
                ⚠️ 데스크탑 전용 처리는 **컴포넌트 안**에 있다. 여기서 `<div className=
                "hidden laptop:block">` 로 감싸면 버튼이 없을 때도 그 div 가 flex 자식으로
                남아 카드 위에 `gap-8`(32) 만큼 빈 공간이 생긴다. */}
            <GameGuideButtons game={game} />
            <GameInfo game={game} />
          </aside>
        </div>
      </div>

      {/* 시안에 없지만 유지 — 위 주석 참조. */}
      <Container className="mx-0 pb-16 laptop:mx-4 laptop:pb-24">
        <SupportedDevices
          devices={game.compatibleDevices}
          gameSlug={game.slug}
        />
      </Container>

      <Container className="mx-0 pb-12 laptop:mx-4 laptop:pb-24">
        <RecommendedGames contentId={game.id} />
      </Container>

      <div className={"mx-4 flex items-end justify-center pb-16 laptop:pb-24"}>
        <div className="flex w-full max-w-[952px] flex-col">
          {game?.gameResell && <GamesDetailFaq />}
          <Inquiry game={game} />
        </div>
      </div>
    </GameDetailPageTemplate>
  );
}

function Container({
  children,
  className,
  ...rest
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("mx-4 flex items-end justify-center", className)}
      {...rest}
    >
      <div className="flex w-full max-w-screen-max flex-col">{children}</div>
    </div>
  );
}

const GamesDetailFaq = () => (
  <>
    <h2 className="w-full pb-5 text-left text-2xl font-400 laptop:pb-8">
      Inquiry
    </h2>
    <BhapticsFaqSection
      tag={FaqNotionTags.GameSelling}
      contents={<FaqHelp />}
    />
  </>
);

export const getServerSideProps: GetServerSideProps = async ({
  locale,
  params,
  req,
  res,
}) => {
  const slug = params?.slug as string;

  // slug 가 유효한 형태(영숫자+하이픈)가 아니면 백엔드 호출 없이 즉시 404.
  // crawler/봇이 Next 라우트 패턴 리터럴(`[slug]`/`[id]`)을 그대로 요청해 by-slug 백엔드가
  // HTML 404 를 반환하던 케이스를 서버 진입 시점에 조기 차단한다 (#4974).
  if (!slug || !/^[a-z0-9-]+$/i.test(slug)) {
    return { notFound: true };
  }

  const resolvedCountryCode = getServerSideCountryCode(
    locale ?? "en",
    req,
    res,
  );
  /**
   * ⚠️ 게임 조회는 `prefetchQuery`(= `getDehydratedState`) 대신 **`fetchQuery`** 로 한다.
   *
   * `prefetchQuery` 는 절대 throw 하지 않고, `dehydrate` 는 **성공한 쿼리만** 담는다(둘 다
   * TanStack Query v5 의 문서화된 동작). 그래서 업스트림 타임아웃이 "데이터 없음"과 구별되지
   * 않고 아래 `notFound` 로 접혀, 백엔드 5초 blip 이 실존 페이지의 하드 404 가 됐다(#5318).
   * TanStack 문서도 이 경우 `fetchQuery` 로 throw 를 받아 "404 냐 5xx 냐" 를 직접 가르라고
   * 안내한다.
   *
   * `retry: 0` 은 이중 재시도 방지 — `admin-api-json` 헬퍼가 이미 `retryOnce` 로 1회
   * 재시도한다. QueryClient 기본값(`retry: 1`)을 그대로 쓰면 최악 대기가 5s×2×2≈20s 가 된다.
   */
  const gameQueryClient = getQueryClient();

  // 게임만 먼저 await 해야 하지만(=throw 를 받아 503/404 를 가른다) **시작은 전부 먼저** 한다.
  // 게임을 단독으로 await 하면 정상 경로 벽시계가 `max(4개)` 에서 `게임 + max(나머지)` 로
  // 늘어난다 — blip 대응 PR 이 정상 경로에 상시 지연을 얹는 형태가 된다.
  const gamePromise = gameQueryClient.fetchQuery({
    ...GameQueryOptionsServer.game(slug),
    retry: 0,
  });
  const translationsPromise = serverSideTranslations(locale ?? "en", [
    "gnb",
    "games",
    "support",
  ]);
  const restPromise = Promise.all([
    getDehydratedState(
      ProductQuery.products(resolvedCountryCode === "US" ? "US" : "KR"),
    ),
    getShippableGeo(req),
  ]);
  // ⚠️ 시작과 await 을 분리했으므로 no-op 핸들러가 **필수**다. 두 경로로 unhandled rejection 이
  //    생긴다: (1) 게임 조회(최악 10s)를 기다리는 동안 이쪽이 먼저 거부, (2) 아래 503 분기로
  //    조기 반환해 `restPromise` 를 아무도 await 하지 않음. 원래 `Promise.all` 은 즉시 전부에
  //    핸들러를 붙여 둘 다 없던 문제다.
  //    핸들러를 붙여도 아래 `await` 은 여전히 거부를 던진다 → 실패 시 500 은 기존과 동일.
  restPromise.catch(() => {});
  translationsPromise.catch(() => {});

  let gameData: BhapticsContent | undefined;
  try {
    gameData = (await gamePromise) ?? undefined;
  } catch (error) {
    if (!isUpstreamUnavailable(error)) throw error;

    // 일시 장애 → 503 + `Retry-After`. 404 로 접으면 색인 해제 위험, 200 + 빈 화면은
    // soft-404 라 더 나쁘다. `redirect` 도 쓰지 않는다(비정규 URL 로 크롤 신호가 흐려진다).
    res.statusCode = 503;
    res.setHeader("Retry-After", "60");
    return {
      props: {
        // 이미 시작해 둔 것을 재사용한다(로컬 파일 로드라 저렴). `restPromise` 는 기다리지
        // 않는다 — 업스트림이 죽은 상황이면 그쪽도 느릴 수 있다.
        ...(await translationsPromise),
        slug,
        countryCode: "",
        errorStatus: 503,
      },
    };
  }

  // 둘 다 이미 진행 중이라 순차 await 이어도 지연이 더해지지 않는다.
  const [productsState, countryCode] = await restPromise;
  const translations = await translationsPromise;
  const gameState = dehydrate(gameQueryClient);

  // 여기 도달 = 업스트림이 정상 응답했고 그 게임이 **없다**. 이때만 404 다.
  // (가드 없이 game.hapticEffects 등 접근 시 client-side 예외가 터지므로 서버에서 처리)
  if (!gameData) {
    return { notFound: true };
  }

  const seo = generateGameSeo(gameData, locale ?? "en");

  const hapticCardEnabled = isHapticCardEnabled(req.headers.host, isProd);
  return {
    props: {
      ...translations,
      dehydratedState: mergeDehydratedStates(gameState, productsState),
      slug,
      countryCode,
      seo,
      // 실서비스 공개 전 — staging·dev·로컬에서만(isHapticCardEnabled 주석). 요약 카드 · 지원 타입 안내 카드 둘 다
      hapticCardEnabled,
      hapticSummary: hapticCardEnabled
        ? getHapticSummary(slug, locale ?? "en")
        : null,
    },
  };
};
